import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';
import { newsApi, categoriesApi } from '../services/api';
import { News, Category } from '../types';
import { getNewsImage, getNewsTitle, getNewsCategoryName, formatNewsDateShort } from '../services/newsHelpers';
import '../styles/NewsSection.css';

const NewsSection: React.FC = () => {
  const [news, setNews] = useState<News[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Busca notícias e categorias em paralelo
        const [newsData, categoriesData] = await Promise.all([
          newsApi.getLatest(4),
          categoriesApi.getActive(),
        ]);

        // Verifica se há dados antes de fazer o map
        if (newsData && newsData.length > 0) {
          // Enriquece as notícias com os dados da categoria
          const enrichedNews = newsData.map(newsItem => {
            const category = categoriesData.find(cat => cat.id === newsItem.categoriaId);
            return {
              ...newsItem,
              categoria: category || null,
            };
          });

          setNews(enrichedNews);
        } else {
          // Se não há notícias, deixa vazio (não mostra a seção)
          setNews([]);
        }

        setCategories(categoriesData);
      } catch (error) {
        console.error('Error loading news:', error);
        // Em caso de erro, deixa vazio
        setNews([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const NewsSkeleton = () => (
    <div className="news-skeleton">
      <div className="news-skeleton-image" />
      <div className="news-skeleton-content">
        <div className="news-skeleton-line" />
        <div className="news-skeleton-line" />
        <div className="news-skeleton-line short" />
      </div>
    </div>
  );

  return (
    <section className="news-section">
      <Container>
        <div className="section-header">
          <span className="section-badge">Fique por dentro</span>
          <h2 className="section-title">Informação Agricampanha</h2>
        </div>

        <Row className="g-4 mt-4">
          {loading ? (
            <>
              {[1, 2, 3, 4].map((i) => (
                <Col key={i} md={6} lg={3}>
                  <NewsSkeleton />
                </Col>
              ))}
            </>
          ) : news.length === 0 ? (
            <Col xs={12}>
              <div className="text-center py-5">
                <i className="bi bi-newspaper" style={{ fontSize: '2.5rem', color: 'var(--color-gray)' }}></i>
                <p className="text-muted mt-3 mb-0">Nenhuma notícia publicada ainda.<br />Em breve, novidades por aqui!</p>
              </div>
            </Col>
          ) : (
            news.map((item) => (
              <Col key={item.id} md={6} lg={3}>
                <Card
                  as={Link}
                  to={`/noticias/${item.slug}`}
                  className="news-card"
                >
                  <div
                    className="news-card-img-wrapper"
                    style={{ backgroundImage: `url(${getNewsImage(item)})` }}
                    role="img"
                    aria-label={getNewsTitle(item)}
                  >
                    <span className="news-card-category">
                      {getNewsCategoryName(item)}
                    </span>
                  </div>
                  <Card.Body className="news-card-body">
                    <div className="news-card-date">
                      {formatNewsDateShort(item.dataNoticia)}
                    </div>
                    <Card.Title className="news-card-title">
                      {getNewsTitle(item)}
                    </Card.Title>
                    <span className="news-card-read-more">
                      Ler mais <span className="arrow">→</span>
                    </span>
                  </Card.Body>
                </Card>
              </Col>
            ))
          )}
        </Row>

        <div className="news-cta">
          <Button
            variant="primary"
            size="lg"
            onClick={() => navigate('/noticias')}
          >
            Ver outras notícias <i className="bi bi-arrow-right ms-1"></i>
          </Button>
        </div>
      </Container>
    </section>
  );
};

export default NewsSection;
